import { useState } from 'react';
import { Rocket, Cpu, Bot, BookOpen, Wrench, Check, ArrowRight, X, CircleCheck, Settings, Sparkles } from 'lucide-react';

// 必做/按需项（与初始化清单一致）
const REQUIRED = [
  { key: 'model', title: '模型接入', icon: Cpu, tab: 'models' },
  { key: 'agent', title: '智能体接入并上架', icon: Bot, tab: 'config' },
];
const OPTIONAL = [
  { key: 'kb', title: '知识库创建', icon: BookOpen, tab: 'knowledge' },
  { key: 'cap', title: '能力注册', icon: Wrench, tab: 'registry' },
];

// 全局右上角 Setup 入口（pill）+ 点开的 checklist 浮层面板
// 适配"无独立首页"的嵌入式模块：不挂在任何页面里，全局常驻、完成后隐退、降级到设置
export default function SetupLauncher({ onNavigate }) {
  const [done, setDone] = useState({});
  const [open, setOpen] = useState(false);     // 浮层是否展开
  const [retired, setRetired] = useState(false); // 必做完成后隐退

  const reqDone = REQUIRED.filter(i => done[i.key]).length;
  const allReqDone = reqDone === REQUIRED.length;
  const phase = retired ? 'retired' : allReqDone ? 'done' : (reqDone > 0 ? 'progress' : 'todo');
  const pct = (reqDone / REQUIRED.length) * 100;

  const setPhase = (ph) => {
    if (ph === 'todo') { setDone({}); setRetired(false); setOpen(true); }
    if (ph === 'progress') { setDone({ model: true }); setRetired(false); setOpen(true); }
    if (ph === 'done') { setDone({ model: true, agent: true }); setRetired(false); setOpen(true); }
    if (ph === 'retired') { setDone({ model: true, agent: true }); setRetired(true); setOpen(false); }
  };

  const go = (tab) => { onNavigate?.(tab); setOpen(false); };

  return (
    <div className="fixed top-3 right-5 z-40 flex flex-col items-end gap-2">
      {/* 入口 pill —— 四态 */}
      {phase === 'retired' ? (
        // 隐退后：降级为极简"设置 · 部署清单"小入口
        <button onClick={() => setRetired(false)} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs text-gray-400 hover:text-gray-600 shadow-sm">
          <Settings className="w-3.5 h-3.5" /> 部署清单
        </button>
      ) : (
        <button onClick={() => setOpen(o => !o)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full shadow-sm border text-sm font-medium transition-colors ${
            phase === 'done' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-indigo-200 text-indigo-700 hover:bg-indigo-50'}`}>
          {phase === 'done' ? <CircleCheck className="w-4 h-4" /> : <Rocket className="w-4 h-4" />}
          <span>{phase === 'done' ? '初始化已就绪' : '部署初始化'}</span>
          {phase !== 'done' && (
            <span className="flex items-center gap-1.5">
              <span className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden"><span className="block h-full bg-indigo-500" style={{ width: `${pct}%` }} /></span>
              <span className="text-xs text-gray-400">{reqDone}/{REQUIRED.length}</span>
            </span>
          )}
        </button>
      )}

      {/* 展开的 checklist 浮层面板 */}
      {open && phase !== 'retired' && (
        <div className="w-80 bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
          <div className={`px-4 py-3 ${phase === 'done' ? 'bg-green-600' : 'bg-gradient-to-r from-indigo-600 to-blue-600'}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-white flex items-center gap-1.5">
                {phase === 'done' ? <CircleCheck className="w-4 h-4" /> : <Rocket className="w-4 h-4" />}
                {phase === 'done' ? '初始化已完成' : '部署初始化清单'}
              </span>
              <button onClick={() => setOpen(false)} className="p-0.5 text-white/80 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex-1 h-1.5 bg-white/30 rounded-full overflow-hidden"><div className="h-full bg-white rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
              <span className="text-xs text-white/90">必做 {reqDone}/{REQUIRED.length}</span>
            </div>
          </div>

          <div className="p-3">
            <p className="text-xs text-gray-500 mb-2">
              {phase === 'done'
                ? '必做项已全部完成，平台可正式对终端用户服务。按需项可随时补做。'
                : '完成必做项后平台即可服务终端用户；前置未满足的项会自动锁定。AI 引导在各配置页内实时提供。'}
            </p>

            <div className="space-y-1.5">
              {[...REQUIRED.map(i => ({ ...i, req: true })), ...OPTIONAL.map(i => ({ ...i, req: false }))].map(it => {
                const d = done[it.key]; const Icon = it.icon;
                return (
                  <div key={it.key} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-xs ${d ? 'bg-green-50 border-green-200' : 'bg-white border-gray-150'}`}>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${d ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-400'}`}>{d ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}</div>
                    <span className={`flex-1 ${d ? 'text-green-700' : 'text-gray-700'}`}>{it.title}</span>
                    <span className={`text-[10px] px-1 py-0.5 rounded ${it.req ? 'bg-red-50 text-red-400' : 'bg-gray-100 text-gray-400'}`}>{it.req ? '必做' : '按需'}</span>
                    {!d && <>
                      <button onClick={() => go(it.tab)} className="text-indigo-500 hover:text-indigo-700">去配置</button>
                      <button onClick={() => setDone(p => ({ ...p, [it.key]: true }))} className="text-gray-300 hover:text-gray-500">✓</button>
                    </>}
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
              <button onClick={() => go('wizard')} className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700">查看完整清单 <ArrowRight className="w-3 h-3" /></button>
              {phase === 'done' && <button onClick={() => { setRetired(true); setOpen(false); }} className="px-2.5 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700">知道了，隐退</button>}
            </div>
          </div>
        </div>
      )}

      {/* demo 预览状态切换器 */}
      <div className="flex items-center gap-1 bg-white/90 border border-gray-200 rounded-full px-2 py-1 text-[11px] text-gray-400 shadow-sm">
        <Sparkles className="w-3 h-3 text-indigo-400" />
        {[['todo', '未完成'], ['progress', '完成中'], ['done', '全完成'], ['retired', '已隐退']].map(([k, l]) => (
          <button key={k} onClick={() => setPhase(k)} className={`px-1.5 py-0.5 rounded ${phase === k ? 'bg-indigo-600 text-white' : 'hover:text-indigo-600'}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}
